import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  Badge,
  CompoundButton,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { PlugConnectedRegular } from '@fluentui/react-icons';
import {
  FixedSizeList,
  type ListChildComponentProps,
  type ListOnItemsRenderedProps,
} from 'react-window';
import type { ConnectorChoice } from './addConnectionTypes';

const CONNECTOR_ROW_HEIGHT = 80;
const DEFAULT_CONNECTOR_LIST_HEIGHT = 480;

const useStyles = makeStyles({
  results: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  row: {
    boxSizing: 'border-box',
    paddingBottom: tokens.spacingVerticalS,
  },
  button: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXXL}`,
  },
  icon: {
    width: '32px',
    height: '32px',
    objectFit: 'contain',
    flexShrink: 0,
    borderRadius: tokens.borderRadiusSmall,
  },
  description: {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
  },
  titleRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
});

interface ConnectorListItemData {
  connectors: ConnectorChoice[];
  onSelect: (connector: ConnectorChoice) => void;
  focusedIndex: number;
  onFocus: (index: number) => void;
  onKeyDown: (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => void;
  rowClassName: string;
  buttonClassName: string;
  iconClassName: string;
  descriptionClassName: string;
  titleRowClassName: string;
}

const ConnectorListRow = ({
  index,
  style,
  data,
}: ListChildComponentProps<ConnectorListItemData>) => {
  const connector = data.connectors[index];
  if (!connector) return null;

  return (
    <div style={style} className={data.rowClassName}>
      <CompoundButton
        className={data.buttonClassName}
        data-connector-index={index}
        tabIndex={index === data.focusedIndex ? 0 : -1}
        onFocus={() => data.onFocus(index)}
        onKeyDown={(event) => data.onKeyDown(event, index)}
        icon={
          connector.icon ? (
            <img
              src={connector.icon}
              alt=""
              aria-hidden="true"
              className={data.iconClassName}
            />
          ) : (
            <PlugConnectedRegular fontSize={32} aria-hidden="true" />
          )
        }
        {...(connector.description
          ? {
              secondaryContent: (
                <span className={data.descriptionClassName}>
                  {connector.description}
                </span>
              ),
            }
          : {})}
        onClick={() => data.onSelect(connector)}
      >
        <span className={data.titleRowClassName}>
          <span>{connector.name}</span>
          {connector.family === 'serviceProvider' && (
            <Badge appearance="tint" color="informative" size="small">
              Built-in
            </Badge>
          )}
        </span>
      </CompoundButton>
    </div>
  );
};

export interface VirtualizedConnectorListProps {
  connectors: ConnectorChoice[];
  onSelect: (connector: ConnectorChoice) => void;
  ariaLabel: string;
  resetKey: string;
}

export const VirtualizedConnectorList = ({
  connectors,
  onSelect,
  ariaLabel,
  resetKey,
}: VirtualizedConnectorListProps) => {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<FixedSizeList<ConnectorListItemData>>(null);
  const pendingFocusIndexRef = useRef<number | null>(null);
  const [height, setHeight] = useState(DEFAULT_CONNECTOR_LIST_HEIGHT);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [renderedRange, setRenderedRange] = useState({
    overscanStartIndex: 0,
    overscanStopIndex: 0,
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateHeight = () => {
      if (container.clientHeight > 0) {
        setHeight(container.clientHeight);
      }
    };

    updateHeight();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    pendingFocusIndexRef.current = null;
    setFocusedIndex(0);
    listRef.current?.scrollToItem(0);
  }, [resetKey]);

  useEffect(() => {
    if (connectors.length === 0) {
      if (focusedIndex !== 0) setFocusedIndex(0);
      return;
    }

    const lastIndex = connectors.length - 1;
    if (focusedIndex > lastIndex) {
      setFocusedIndex(lastIndex);
      listRef.current?.scrollToItem(lastIndex, 'smart');
    }
  }, [connectors.length, focusedIndex]);

  const focusRenderedItem = useCallback((index: number): boolean => {
    const target = containerRef.current?.querySelector<HTMLButtonElement>(
      `[data-connector-index="${index}"]`,
    );
    if (!target) return false;
    target.focus();
    pendingFocusIndexRef.current = null;
    return true;
  }, []);

  const handleItemsRendered = useCallback(
    ({ overscanStartIndex, overscanStopIndex }: ListOnItemsRenderedProps) => {
      setRenderedRange({ overscanStartIndex, overscanStopIndex });
    },
    [],
  );

  useLayoutEffect(() => {
    const pendingIndex = pendingFocusIndexRef.current;
    if (
      pendingIndex === null ||
      pendingIndex < renderedRange.overscanStartIndex ||
      pendingIndex > renderedRange.overscanStopIndex
    ) {
      return;
    }
    focusRenderedItem(pendingIndex);
  }, [focusRenderedItem, renderedRange]);

  const focusItem = useCallback(
    (index: number) => {
      const nextIndex = Math.max(0, Math.min(index, connectors.length - 1));
      setFocusedIndex(nextIndex);
      pendingFocusIndexRef.current = nextIndex;
      if (focusRenderedItem(nextIndex)) return;
      listRef.current?.scrollToItem(nextIndex, 'smart');
    },
    [connectors.length, focusRenderedItem],
  );

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
      let nextIndex: number | null = null;
      switch (event.key) {
        case 'ArrowDown':
          nextIndex = index + 1;
          break;
        case 'ArrowUp':
          nextIndex = index - 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = connectors.length - 1;
          break;
      }
      if (nextIndex === null) return;
      event.preventDefault();
      focusItem(nextIndex);
    },
    [connectors.length, focusItem],
  );

  const itemData = useMemo<ConnectorListItemData>(
    () => ({
      connectors,
      onSelect,
      focusedIndex,
      onFocus: setFocusedIndex,
      onKeyDown: handleKeyDown,
      rowClassName: styles.row,
      buttonClassName: styles.button,
      iconClassName: styles.icon,
      descriptionClassName: styles.description,
      titleRowClassName: styles.titleRow,
    }),
    [connectors, focusedIndex, handleKeyDown, onSelect, styles],
  );

  return (
    <div
      ref={containerRef}
      className={styles.results}
      role="group"
      aria-label={ariaLabel}
    >
      <FixedSizeList
        ref={listRef}
        key={resetKey}
        height={height}
        itemCount={connectors.length}
        itemData={itemData}
        itemSize={CONNECTOR_ROW_HEIGHT}
        onItemsRendered={handleItemsRendered}
        width="100%"
      >
        {ConnectorListRow}
      </FixedSizeList>
    </div>
  );
};
